import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import useUserPlatform from '@/hooks/use-user-platform';
import { makePrompt } from '@/stores/prompter';
import { BUILD_EMBED_TYPE } from '@/utils/constants';
import { FileType } from '@/utils/file-type';

interface PickMediaFileOptions {
  type?: 'camera' | 'album';
  accept?: string;
  limit?: number;
}

export default function usePickMediaFile() {
  const { t } = useTranslation();
  const { isIOS } = useUserPlatform();
  const inputRef = useRef<HTMLInputElement | null>(null);

  const pickMedia = useCallback(async ({ type, accept, limit = 1 }: PickMediaFileOptions) => {
    if (!inputRef.current) return;

    inputRef.current.removeAttribute('multiple');
    inputRef.current.removeAttribute('maxlength');
    inputRef.current.removeAttribute('accept');
    inputRef.current.removeAttribute('capture');

    if (type === 'camera') {
      // NOTE: ask for permission only when using camera and on native embed
      if (BUILD_EMBED_TYPE === 'embed-native') {
        await navigator.mediaDevices.getUserMedia({ video: true }).then((stream) => {
          for (const track of stream.getTracks()) {
            track.stop();
          }
        });
      }

      inputRef.current.setAttribute('capture', 'camera');
    } else {
      inputRef.current.removeAttribute('capture');
    }

    if (accept) {
      inputRef.current.setAttribute('accept', accept);
    } else {
      inputRef.current.removeAttribute('accept');
    }

    inputRef.current.setAttribute('maxlength', limit.toString());

    if (limit > 1) {
      inputRef.current.setAttribute('multiple', 'multiple');
    }

    inputRef.current.click();
  }, []);

  const clearMedia = useCallback(() => {
    if (!inputRef.current) return;

    inputRef.current.value = '';
    inputRef.current.files = null;
    inputRef.current.removeAttribute('multiple');
    inputRef.current.removeAttribute('maxlength');
    inputRef.current.removeAttribute('accept');
    inputRef.current.removeAttribute('capture');
  }, []);

  const promptPickMedia = async () => {
    if (isIOS) {
      return pickMedia({ accept: FileType.Image });
    }

    const resolution = await makePrompt({
      key: 'pick-media',
      type: 'action-sheet',
      title: 'Pick media',
      resolutionButtons: ['camera', 'album'].map((item) => ({
        label: t(`upload.${item}`),
        resolution: item,
      })),
    });

    if (typeof resolution === 'string') {
      return pickMedia({ accept: FileType.Image, type: resolution as 'camera' | 'album' });
    }
  };

  return {
    ref: inputRef,
    pickMedia,
    promptPickMedia,
    clearMedia,
  } as const;
}
